import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { AlertCircle, Megaphone, Plus, Trash2, Edit2, X, Eye, ExternalLink } from 'lucide-react';
import { Alert, AlertDescription } from '../../ui/alert';
import { toast } from 'sonner';
import { supabase } from '../../../lib/supabase';

interface Announcement {
  id: string;
  title: string;
  description: string;
  target_date: string;
  link_url: string;
  is_active: boolean;
  target_type: 'all' | 'role' | 'user' | 'subscription';
  target_roles: string[];
  target_users: string[];
  target_subscriptions: string[];
  max_shows: number;
  created_at: string;
}

interface UserMeta {
  id: string;
  email: string;
  full_name: string;
  role: string;
}

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? '';
}

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
}

export function AnnouncementControl() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewAnnouncement, setPreviewAnnouncement] = useState<Announcement | null>(null);

  // Meta for dropdowns
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [availableUsers, setAvailableUsers] = useState<UserMeta[]>([]);
  const [availablePlans, setAvailablePlans] = useState<SubscriptionPlan[]>([]);
  const [userSearch, setUserSearch] = useState('');

  // Form State
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [targetType, setTargetType] = useState<'all' | 'role' | 'user' | 'subscription'>('all');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserMeta[]>([]);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState<string[]>([]);
  const [maxShows, setMaxShows] = useState<number>(1);

  const fetchAnnouncements = async () => {
    setIsLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/announcement?t=${Date.now()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-store'
      });
      const data = await response.json();
      if (data.ok) {
        setAnnouncements(data.data ?? []);
      } else {
        toast.error(data.error || 'Failed to fetch announcements');
      }
    } catch (error) {
      console.error(error);
      toast.error('Network error while fetching announcements');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMeta = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/announcement/meta`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.ok) {
        setAvailableRoles(data.data.roles ?? []);
        setAvailableUsers(data.data.users ?? []);
        setAvailablePlans(data.data.plans ?? []);
      }
    } catch (error) {
      console.error('Failed to fetch meta:', error);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenDialog = (announcement?: Announcement) => {
    if (announcement) {
      setEditingId(announcement.id);
      setTitle(announcement.title ?? '');
      setDescription(announcement.description ?? '');
      setTargetDate(announcement.target_date ? (new Date(announcement.target_date).toISOString().split('T')[0] ?? '') : '');
      setLinkUrl(announcement.link_url ?? '');
      setIsActive(announcement.is_active);
      setTargetType(announcement.target_type ?? 'all');
      setSelectedRoles(announcement.target_roles ?? []);
      setSelectedSubscriptions(announcement.target_subscriptions ?? []);
      setMaxShows(announcement.max_shows ?? 1);
      // Match saved user IDs/emails back to UserMeta objects
      const savedUsers = availableUsers.filter(u =>
        (announcement.target_users ?? []).includes(u.id) ||
        (announcement.target_users ?? []).includes(u.email)
      );
      setSelectedUsers(savedUsers);
    } else {
      setEditingId(null);
      setTitle('');
      setDescription('');
      setTargetDate('');
      setLinkUrl('');
      setIsActive(true);
      setTargetType('all');
      setSelectedRoles([]);
      setSelectedUsers([]);
      setSelectedSubscriptions([]);
      setMaxShows(1);
    }
    setUserSearch('');
    setIsDialogOpen(true);
  };

  const toggleRole = (role: string) => {
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const toggleUser = (u: UserMeta) => {
    setSelectedUsers(prev => 
      prev.some(x => x.id === u.id) ? prev.filter(x => x.id !== u.id) : [...prev, u]
    );
  };

  const toggleSubscription = (subId: string) => {
    setSelectedSubscriptions(prev => 
      prev.includes(subId) ? prev.filter(x => x !== subId) : [...prev, subId]
    );
  };

  const filteredUsers = availableUsers.filter(u =>
    userSearch === '' ||
    u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
    (u.full_name || '').toLowerCase().includes(userSearch.toLowerCase())
  );

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    try {
      const token = await getToken();
      const payload = {
        id: editingId,
        title: title.trim(),
        description: description.trim(),
        target_date: targetDate || null,
        link_url: linkUrl.trim() || null,
        is_active: isActive,
        target_type: targetType,
        target_roles: targetType === 'role' ? selectedRoles : [],
        target_users: targetType === 'user' ? selectedUsers.map(u => u.id) : [],
        target_subscriptions: targetType === 'subscription' ? selectedSubscriptions : [],
        max_shows: maxShows,
      };

      const response = await fetch(`${API_URL}/api/announcement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.ok) {
        toast.success(editingId ? 'Announcement updated' : 'Announcement created');
        setIsDialogOpen(false);
        fetchAnnouncements();
      } else {
        toast.error(data.error || 'Failed to save announcement');
      }
    } catch (error) {
      console.error(error);
      toast.error('Network error while saving announcement');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/announcement/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.ok) {
        toast.success('Announcement deleted');
        fetchAnnouncements();
      } else {
        toast.error(data.error || 'Failed to delete announcement');
      }
    } catch (error) {
      console.error(error);
      toast.error('Network error while deleting announcement');
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-blue-500" />
            Announcement Popup Manager
          </CardTitle>
          <CardDescription>
            Create and manage global popup announcements for users.
          </CardDescription>
        </div>
        <Button onClick={() => handleOpenDialog()} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          New Popup
        </Button>
      </CardHeader>
      <CardContent>
        {announcements.length === 0 && !isLoading ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>No Announcements</strong> — You haven't created any announcements yet. Click 'New Popup' to create one.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Audience</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {announcements.map((ann) => (
                  <TableRow key={ann.id}>
                    <TableCell className="font-medium">{ann.title}</TableCell>
                    <TableCell>
                      {ann.target_type === 'all' && 'All Users'}
                      {ann.target_type === 'role' && `Roles: ${ann.target_roles?.join(', ')}`}
                      {ann.target_type === 'user' && `Specific Users (${ann.target_users?.length})`}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${ann.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'}`}>
                        {ann.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>{new Date(ann.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right space-x-2 whitespace-nowrap">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        title="Preview"
                        onClick={() => {
                          setPreviewAnnouncement(ann);
                          setIsPreviewOpen(true);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" title="Edit" onClick={() => handleOpenDialog(ann)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="destructive" size="sm" title="Delete" onClick={() => handleDelete(ann.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Announcement' : 'Create Announcement'}</DialogTitle>
              <DialogDescription>
                Configure the popup notification details and audience targeting.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="ann-title">Title <span className="text-red-500">*</span></Label>
                <Input
                  id="ann-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. New Feature: Broker Summary Pro"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="ann-description">Description</Label>
                <textarea
                  id="ann-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Explain the new feature or announcement..."
                  className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              {/* Date, Link & Max Shows */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 items-end">
                <div className="space-y-2">
                  <Label htmlFor="ann-targetDate" className="whitespace-nowrap">Target Date (Optional)</Label>
                  <Input 
                    type="date" 
                    id="ann-targetDate" 
                    value={targetDate} 
                    onChange={(e) => setTargetDate(e.target.value)} 
                    className="w-full [color-scheme:light] dark:[color-scheme:dark]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ann-linkUrl" className="whitespace-nowrap">Action Link (Optional)</Label>
                  <Input id="ann-linkUrl" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://..." />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ann-maxShows" className="whitespace-nowrap">Max Shows per User</Label>
                  <Input 
                    type="number" 
                    id="ann-maxShows" 
                    min={1} 
                    max={100} 
                    value={maxShows} 
                    onChange={(e) => setMaxShows(parseInt(e.target.value) || 1)} 
                  />
                </div>
              </div>

              {/* Audience Targeting */}
              <div className="space-y-3 border rounded-md p-4 bg-muted/30">
                <div className="font-medium text-sm">Audience Targeting</div>

                <div className="space-y-2">
                  <Label>Who should see this?</Label>
                  <Select value={targetType} onValueChange={(val: 'all' | 'role' | 'user' | 'subscription') => { setTargetType(val); setSelectedRoles([]); setSelectedUsers([]); setSelectedSubscriptions([]); }}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Users</SelectItem>
                      <SelectItem value="role">Specific Roles</SelectItem>
                      <SelectItem value="user">Specific Users</SelectItem>
                      <SelectItem value="subscription">Specific Subscription</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Role multi-select */}
                {targetType === 'role' && (
                  <div className="space-y-2">
                    <Label>Select Roles</Label>
                    <div className="flex flex-wrap gap-2 min-h-[36px] rounded-md border border-input bg-background p-2">
                      {availableRoles.map(role => (
                        <button
                          key={role}
                          type="button"
                          onClick={() => toggleRole(role)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            selectedRoles.includes(role)
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                        >
                          {role}
                          {selectedRoles.includes(role) && <X className="inline w-3 h-3 ml-1" />}
                        </button>
                      ))}
                      {availableRoles.length === 0 && <span className="text-xs text-muted-foreground">No roles found</span>}
                    </div>
                  </div>
                )}

                {/* User multi-select with search */}
                {targetType === 'user' && (
                  <div className="space-y-2">
                    <Label>Select Users</Label>
                    {/* Selected users chips */}
                    {selectedUsers.length > 0 && (
                      <div className="flex flex-wrap gap-1 p-2 rounded-md border border-input bg-background min-h-[36px]">
                        {selectedUsers.map(u => (
                          <span key={u.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary text-primary-foreground">
                            {u.email}
                            <button type="button" onClick={() => toggleUser(u)}><X className="w-3 h-3" /></button>
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Search */}
                    <Input
                      placeholder="Search by email or name..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                    />
                    {/* User list */}
                    <div className="max-h-48 overflow-y-auto rounded-md border border-input bg-background divide-y divide-border">
                      {filteredUsers.length === 0 && (
                        <p className="text-xs text-muted-foreground p-3">No users found</p>
                      )}
                      {filteredUsers.map(u => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => toggleUser(u)}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors flex items-center justify-between ${
                            selectedUsers.some(su => su.id === u.id) ? 'bg-primary/10' : ''
                          }`}
                        >
                          <span>
                            <span className="font-medium">{u.full_name || u.email}</span>
                            <span className="text-xs text-muted-foreground ml-2">{u.email}</span>
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            u.role === 'admin' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                            u.role === 'developer' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                            'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                          }`}>{u.role}</span>
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">{selectedUsers.length} user(s) selected</p>
                  </div>
                )}

                {/* Subscription multi-select */}
                {targetType === 'subscription' && (
                  <div className="space-y-2">
                    <Label>Select Subscriptions</Label>
                    <div className="flex flex-wrap gap-2 min-h-[36px] rounded-md border border-input bg-background p-2">
                      {/* Special fixed options */}
                      {[
                        { id: 'free', name: 'Free Users (No Active Sub)' },
                        { id: 'all_pro', name: 'All Pro (Any Paid Plan)' }
                      ].map(sub => (
                        <button
                          key={sub.id}
                          type="button"
                          onClick={() => toggleSubscription(sub.id)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            selectedSubscriptions.includes(sub.id)
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                        >
                          {sub.name}
                          {selectedSubscriptions.includes(sub.id) && <X className="inline w-3 h-3 ml-1" />}
                        </button>
                      ))}
                      
                      {/* Available Plans from DB */}
                      {availablePlans.map(plan => (
                        <button
                          key={plan.id}
                          type="button"
                          onClick={() => toggleSubscription(plan.id)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border border-blue-500/20 ${
                            selectedSubscriptions.includes(plan.id)
                              ? 'bg-blue-600 text-white'
                              : 'bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20'
                          }`}
                        >
                          {plan.name}
                          {selectedSubscriptions.includes(plan.id) && <X className="inline w-3 h-3 ml-1" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Active toggle */}
              <div className="flex items-center space-x-2 pt-2">
                <input
                  type="checkbox"
                  id="ann-isActive"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="h-4 w-4 rounded border-input"
                />
                <Label htmlFor="ann-isActive">Active (Will be shown to users immediately)</Label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave}>Save Announcement</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          {previewAnnouncement && (
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader className="flex flex-col items-center space-y-4 pt-4">
                <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-2">
                  <Megaphone className="w-8 h-8 text-blue-500" />
                </div>
                <DialogTitle className="text-2xl font-bold text-center w-full">
                  {previewAnnouncement.title}
                </DialogTitle>
                {previewAnnouncement.target_date && (
                  <div className="text-sm font-semibold text-muted-foreground bg-muted px-3 py-1 rounded-full border">
                    {new Date(previewAnnouncement.target_date).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                )}
              </DialogHeader>
              
              <div className="py-4">
                <DialogDescription className="text-base text-center leading-relaxed whitespace-pre-wrap">
                  {previewAnnouncement.description}
                </DialogDescription>
              </div>

              <DialogFooter className="flex flex-col sm:flex-col gap-3 mt-4">
                {previewAnnouncement.link_url && (
                  <Button 
                    className="w-full text-md h-12" 
                    onClick={() => {
                      if(previewAnnouncement.link_url) {
                        window.open(previewAnnouncement.link_url, '_blank');
                      }
                      setIsPreviewOpen(false);
                    }}
                  >
                    Cek Sekarang
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                )}
                <Button 
                  variant={previewAnnouncement.link_url ? "outline" : "default"} 
                  className="w-full text-md h-12" 
                  onClick={() => setIsPreviewOpen(false)}
                >
                  OK, Mengerti
                </Button>
              </DialogFooter>
            </DialogContent>
          )}
        </Dialog>
      </CardContent>
    </Card>
  );
}
